import { useState, useEffect } from 'react'
import axios from 'axios'
import { BASE_URL } from '../apiConfig'
import Cookies from 'js-cookie'
import { toast } from 'sonner'
import { useTranslation } from '../LanguageContext'
import { Edit, X, Plus, Trash } from 'lucide-react'

export default function InvoiceEditModal({ invoice, products, onClose, onUpdated }) {
  const { t } = useTranslation()
  const token = Cookies.get('WEB_SSR')
  const [saving, setSaving] = useState(false)
  const [clientName, setClientName] = useState(invoice.client_name || '')
  const [clientPhone, setClientPhone] = useState(invoice.client_phone || '')
  const [status, setStatus] = useState(invoice.status || 'not_yet')
  const [remise, setRemise] = useState(invoice.remise ? Number(invoice.remise) : '')
  const [items, setItems] = useState(
    (invoice.items || []).map(item => ({
      id: item.id,
      product_id: item.product_id || '',
      product_variant_id: item.product_variant_id || '',
      product_name: item.product_name,
      quantity: item.quantity,
      unit_price: item.unit_price,
      line_total: Number(item.line_total) || 0,
    }))
  )

  const handleProductChange = (index, productId) => {
    const product = products.find(p => p.id === Number(productId))
    setItems(prev => {
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        product_id: productId,
        product_variant_id: '',
        product_name: product ? product.name : '',
        unit_price: product ? Number(product.price) : 0,
        line_total: product ? Number(product.price) * updated[index].quantity : 0,
      }
      return updated
    })
  }

  const handleVariantChange = (index, variantId) => {
    const product = products.find(p => p.id === Number(items[index].product_id))
    const variant = product?.variants?.find(v => v.id === Number(variantId))
    setItems(prev => {
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        product_variant_id: variantId,
        product_name: product ? product.name : updated[index].product_name,
        unit_price: product ? Number(product.price) : (Number(updated[index].unit_price) || 0),
        line_total: product ? Number(product.price) * updated[index].quantity : 0,
      }
      return updated
    })
  }

  const handleQtyChange = (index, qty) => {
    const num = Math.max(1, parseInt(qty) || 1)
    setItems(prev => {
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        quantity: num,
        line_total: (updated[index].unit_price || 0) * num,
      }
      return updated
    })
  }

  const handlePriceChange = (index, price) => {
    const num = parseFloat(price) || 0
    setItems(prev => {
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        unit_price: num,
        line_total: num * updated[index].quantity,
      }
      return updated
    })
  }

  const addItem = () => {
    setItems(prev => [...prev, { id: null, product_id: '', product_variant_id: '', product_name: '', quantity: 1, unit_price: 0, line_total: 0 }])
  }

  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index))
  }

  const subtotal = items.reduce((sum, item) => sum + item.line_total, 0)
  const remiseValue = Number(remise) || 0
  const total = Math.max(subtotal - remiseValue, 0)

  const handleSubmit = async () => {
    if (!clientName.trim()) {
      toast.error(t('invoices.validation.client_name'))
      return
    }
    if (items.length === 0) {
      toast.error('At least one item is required')
      return
    }

    setSaving(true)
    let response
    try {
      const payload = {
        client_name: clientName,
        client_phone: clientPhone,
        status,
        remise: remiseValue || null,
        items: items.map(item => ({
          id: item.id,
          product_id: item.product_id || null,
          product_variant_id: item.product_variant_id || null,
          product_name: item.product_name,
          quantity: item.quantity,
      unit_price: Number(item.unit_price) || 0,
        })),
      }

      response = await axios.put(`${BASE_URL}/api/invoices/${invoice.id}`, payload, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })

      if (response.data.success) {
        toast.success(t('invoices.update_success'))
        window.dispatchEvent(new Event('stock-updated'))
      }
    } catch (error) {
      console.error('InvoiceEditModal save error:', error)
      const errMsg = error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().filter(Boolean)[0]
        : (error.response?.data?.message || t('invoices.error.general'))
      toast.error(errMsg)
    } finally {
      setSaving(false)
      if (response?.data?.success) {
        onUpdated?.()
        onClose()
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-xl bg-white shadow-xl dark:bg-gray-800 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-gray-100 p-2 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400">
              <Edit className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('invoices.edit')} — {invoice.invoice_code}</h2>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('invoices.client_name')}</label>
              <input
                type="text"
                value={clientName}
                onChange={e => setClientName(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('invoices.client_phone')}</label>
              <input
                type="text"
                value={clientPhone}
                onChange={e => setClientPhone(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('invoices.status')}</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="not_yet">{t('invoices.status.not_yet')}</option>
                <option value="regle">{t('invoices.status.regle')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('invoices.remise')}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={remise}
                onChange={e => setRemise(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-xs font-medium uppercase text-gray-500">{t('invoices.product')}</th>
                  <th className="px-4 py-3 text-xs font-medium uppercase text-gray-500">{t('invoices.variant')}</th>
                  <th className="px-4 py-3 text-xs font-medium uppercase text-gray-500">{t('invoices.quantity')}</th>
                  <th className="px-4 py-3 text-xs font-medium uppercase text-gray-500">{t('invoices.unit_price')}</th>
                  <th className="px-4 py-3 text-xs font-medium uppercase text-gray-500">{t('invoices.line_total')}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const product = products.find(p => p.id === Number(item.product_id))
                  const variants = product?.variants || []
                  return (
                    <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                      <td className="px-4 py-3">
                        <select
                          value={item.product_id}
                          onChange={e => handleProductChange(index, e.target.value)}
                          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                        >
                          <option value="">{t('invoices.select_product')}</option>
                          {products.map(p => (
                            <option key={p.id} value={p.id}>{p.name} — {p.price}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {variants.length > 0 ? (
                          <select
                            value={item.product_variant_id}
                            onChange={e => handleVariantChange(index, e.target.value)}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                          >
                            <option value="">{t('invoices.select_variant')}</option>
                            {variants.map(v => (
                              <option key={v.id} value={v.id}>{v.display_name || `${v.color || ''}${v.color && v.size ? ' / ' : ''}${v.size || ''}`}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs text-gray-400">{t('invoices.no_variant')}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={e => handleQtyChange(index, e.target.value)}
                          className="w-20 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unit_price}
                          onChange={e => handlePriceChange(index, e.target.value)}
                          className="w-24 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                        />
                      </td>
                      <td className="px-4 py-3 font-medium">{item.line_total.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="p-1.5 text-red-400 hover:text-red-600"
                        >
                          <Trash className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="6" className="px-4 py-3">
                    <button
                      type="button"
                      onClick={addItem}
                      className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-700"
                    >
                      <Plus className="h-4 w-4" />
                      {t('invoices.add_item')}
                    </button>
                  </td>
                </tr>
                <tr className="border-t border-gray-200 dark:border-gray-700">
                  <td colSpan="4" className="px-4 py-2 text-right text-sm text-gray-600 dark:text-gray-400">{t('invoices.subtotal')}</td>
                  <td className="px-4 py-2 text-sm">{subtotal.toFixed(2)}</td>
                  <td></td>
                </tr>
                {remiseValue > 0 && (
                  <tr className="border-t border-gray-200 dark:border-gray-700">
                    <td colSpan="4" className="px-4 py-2 text-right text-sm text-red-600 dark:text-red-400">{t('invoices.remise')}</td>
                    <td className="px-4 py-2 text-sm text-red-600 dark:text-red-400">-{remiseValue.toFixed(2)}</td>
                    <td></td>
                  </tr>
                )}
                <tr className="border-t-2 border-gray-300 dark:border-gray-600">
                  <td colSpan="4" className="px-4 py-3 text-right text-sm font-semibold">{t('invoices.total')}</td>
                  <td className="px-4 py-3 font-bold">{total.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {t('invoices.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="rounded-lg bg-stone-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-50"
          >
            {saving ? t('invoices.saving') : t('invoices.save_edit')}
          </button>
        </div>
      </div>
    </div>
  )
}
